// lib/features/filters/filters.types.ts
import { ApiResponse } from "../../api/api.types";

export interface FilterRequest {
  section_id: number;
  seque: string;
  search?: string;
  gender?: number[];
  category?: number[];
  category_groups?: number[];
  category_sub_groups?: number[];
  /** Yalnızca slug bağlamından gelir (item_type: "product"); sidebar'da karşılığı yok. */
  product?: number[];
  color?: string[];
  size?: string[];
  brand?: number[];
  season?: number[];
  seller_badge?: number[];
  rating?: number[];
  discounted?: boolean;
  new_products?: boolean;
  coupon?: boolean;
  bulk_sale?: boolean;
  is_advantageous?: boolean;
  flash_products?: boolean;
  price?: { min?: number; max?: number };
  sort?: string;
  special_filters?: string[];
  item_type?: string;
  item_ids?: number[];
  menu_type?: string;
}

// ─── Shared item shapes ───────────────────────────────────────────────────────

export interface FilterListItem {
  id: number | string;
  name: string;
  selected: boolean;
  count?: number;
}

export interface FilterColorListItem {
  id: number | string;
  name: string;
  hex: string;
  selected: boolean;
  count?: number;
}

// ─── POST /products/filters ───────────────────────────────────────────────────

export interface FilterListGroup {
  row_number: number;
  title: string;
  type: "list";
  search: boolean;
  items: FilterListItem[] | FilterColorListItem[];
  key: ListFilterKey;
  value: (number | string)[];
  selected_count: number;
}

export interface FilterInputGroup {
  row_number: number;
  title: string;
  type: "input";
  key: "price";
  value: { min: number | null; max: number | null };
  min: number;
  max: number;
  min_default: string;
  max_default: string;
  selected_count: number;
  description: string;
}

export type FilterGeneralItem = FilterListGroup | FilterInputGroup;

export interface FilterHeaderItem {
  row_number: number;
  title: string;
  type: "checkbox";
  key: BooleanFilterKey;
  value: boolean;
  count?: number;
  selected_count: number;
}

export interface FiltersData {
  filters: {
    header?: FilterHeaderItem[];
    general: FilterGeneralItem[];
  };
  product_count: number;
}

export type FiltersResponse = ApiResponse<FiltersData>;

// ─── GET /general/filters ─────────────────────────────────────────────────────

export interface SortOption {
  key: string;
  label: string;
}

export interface SpecialFilterItem {
  key: string;
  label: string;
  icon: string;
  color: string;
  type: "checkbox";
  value: boolean;
  count: number;
  selected_count: number;
}

/** Items in /general/filters brand list have string IDs + count. */
export interface GeneralFilterListItem {
  id: string;
  name: string;
  selected: boolean;
  count: number;
}

/** Color items from /general/filters (if ever returned). */
export interface GeneralFilterColorItem {
  id: string;
  name: string;
  hex: string;
  selected: boolean;
  count: number;
}

export interface GeneralFilterList {
  row_number: number;
  title: string;
  type: "list";
  search: boolean;
  items: (GeneralFilterListItem | GeneralFilterColorItem)[];
  key: GeneralListFilterKey;
  value: string[];
  selected_count: number;
}

export interface GeneralFilterInput {
  row_number: number;
  title: string;
  type: "input";
  key: "price";
  value: { min: number | null; max: number | null };
  min: number;
  max: number;
  min_default: string;
  max_default: string;
  selected_count: number;
  description: string;
}

export type GeneralFilterItem = GeneralFilterList | GeneralFilterInput;

export interface GeneralFiltersData {
  sort_options: SortOption[];
  special_filters: SpecialFilterItem[];
  filters: GeneralFilterItem[];
  product_count: number;
}

export type GeneralFiltersResponse = ApiResponse<GeneralFiltersData>;

export interface SelectedFilters {
  gender: number[];
  category: number[];
  category_groups: number[];
  category_sub_groups: number[];
  /** Slug kontrolünden gelen bağlam key'i. ListFilterKey DEĞİL: backend bu adla
   *  bir filtre grubu döndürmüyor, kullanıcı toggle'layamıyor. */
  product: number[];
  color: string[];
  size: string[];
  brand: number[];
  season: number[];
  seller_badge: number[];
  rating: number[];
  discounted: boolean;
  new_products: boolean;
  coupon: boolean;
  bulk_sale: boolean;
  price: { min?: number; max?: number };
  sort: string;
  special_filters: string[];
}

export type ListFilterKey =
  | "gender"
  | "category"
  | "category_groups"
  | "category_sub_groups"
  | "color"
  | "size"
  | "brand"
  | "season"
  | "seller_badge"
  | "rating";

export type GeneralListFilterKey = "color" | "brand";

export type BooleanFilterKey =
  | "discounted"
  | "new_products"
  | "coupon"
  | "bulk_sale";

export interface GeneralFiltersRequest {
  seque?: string;
  item_type?: string;
  item_ids?: number[];
  gender?: number[];
  context_category?: number[];
  search?: string;
}

export const DEFAULT_FILTER_REQUEST: Pick<
  FilterRequest,
  "section_id" | "seque"
> = {
  section_id: -1,
  seque: "PRODUCTSEQUE",
};

/** Keys whose item IDs are stored/sent as strings (not numbers). */
export const STRING_FILTER_KEYS = new Set<keyof SelectedFilters>(["size", "color"]);

export const isColorItem = (item: object): item is FilterColorListItem =>
  "hex" in item;

export const isLightColor = (hex: string): boolean => {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return true;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 160;
};
